import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Loader2, Download, ShieldCheck } from "lucide-react";
import DOMPurify from "dompurify";

function renderMarkdown(md) {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) => DOMPurify.sanitize(esc(s).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>"));
  return (md || "").split("\n").map((line, i) => {
    const t = line.trim();
    const k = `${i}-${t.slice(0, 24)}`;
    if (!t) return <div key={k} className="h-3" />;
    if (t.startsWith("# ")) return <h1 key={k} className="font-heading font-bold text-navy text-xl mt-3" dangerouslySetInnerHTML={{ __html: inline(t.slice(2)) }} />;
    if (t.startsWith("## ")) return <h2 key={k} className="font-heading font-semibold text-navy text-lg mt-3" dangerouslySetInnerHTML={{ __html: inline(t.slice(3)) }} />;
    return <p key={k} className="text-sm text-[#334155] leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(t) }} />;
  });
}

export default function PublicDisputeLetter() {
  const { token } = useParams();
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);

  useEffect(() => {
    document.title = "TenantSeal Dispute Letter";
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m);
    axios.get(`${API}/dispute-letters/shared/${token}`)
      .then(({ data }) => { setData(data); setState("ok"); })
      .catch((err) => { console.error("public dispute letter load failed", err); setState("notfound"); });
    return () => { try { document.head.removeChild(m); } catch { /* noop */ } };
  }, [token]);

  if (state === "loading") return <div className="min-h-screen grid place-items-center bg-[#EEF2F6]"><Loader2 className="animate-spin text-brand" /></div>;
  if (state === "notfound") return (
    <div className="min-h-screen grid place-items-center bg-[#EEF2F6] px-6 text-center">
      <div><h1 className="font-heading text-2xl font-bold text-navy">Letter not found</h1><p className="mt-2 text-[#475569]">This link is invalid or the letter is no longer available.</p></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#EEF2F6]">
      <header className="border-b border-[#E2E8F0] bg-white/85 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo />
          <a href={`${API}/dispute-letters/shared/${token}/pdf`} target="_blank" rel="noreferrer">
            <Button data-testid="public-download-pdf" className="rounded-xl bg-navy hover:bg-navy-hover text-white h-10"><Download size={16} className="mr-2" /> Download PDF</Button>
          </a>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 text-xs text-[#94A3B8] mb-4"><ShieldCheck size={14} className="text-amber" /> Read-only security deposit dispute letter</div>
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 sm:p-8" data-testid="public-letter-body">{renderMarkdown(data.letter_body_markdown)}</div>
        <p className="mt-6 text-center text-xs text-[#94A3B8]">Prepared with TenantSeal · document-preparation tool, not legal advice.</p>
      </main>
    </div>
  );
}
